import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subject, debounceTime, filter, of, takeUntil } from 'rxjs';

import { ApiTesterControlsComponent } from './widgets/api-tester-controls/api-tester-controls.component';
import { CreateKeyComponent } from './widgets/create-key/create-key.component';
import { FilterComponent } from './widgets/filter/filter.component';
import { FormsModule } from '@angular/forms';
import { IntrospectKeysWidgetComponent } from './widgets/introspect-keys-widget/introspect-keys-widget.component';
import { MatButtonModule } from '@angular/material/button';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { navItemConstants } from '@core/constants';

@Component({
    selector: 'rh-sidenav',
    imports: [
        ApiTesterControlsComponent,
        AsyncPipe,
        CommonModule,
        CreateKeyComponent,
        FilterComponent,
        FormsModule,
        IntrospectKeysWidgetComponent,
        MatButtonModule,
        MatGridListModule,
        MatIconModule,
        MatListModule,
        MatMenuModule,
        MatSidenavModule,
        MatToolbarModule,
        MatTooltipModule,
        RouterModule,
    ],
    templateUrl: './sidenav.component.html',
    styleUrl: './sidenav.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavComponent implements OnDestroy {
  @Input() showDebugTools = false;
  @Input({ required: true }) userEmail?: string;
  @Output() loginOutAction = new EventEmitter();

  private readonly router = inject(Router);
  public activePage$ = new Subject();
  public activeRoute$ = new Subject();
  public isAuthenticated$ = of(true);
  public isChecked = false;
  public isCollapsed = false;
  public hasWidgets = [
    '/api-tester',
    '/create-key',
    '/dashboard',
    '/introspect-key',
  ];
  public menuItems = (showDebugTools: boolean) =>
    navItemConstants(showDebugTools);
  private readonly onDestroy = new Subject();

  ngAfterViewInit(): void {
    this.isCollapsed = true;
    this.setActivePage();
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  public onNavigation(item: any): void {
    this.router.navigate([item.route]);
  }

  public isActive(route: string): boolean {
    return this.router.isActive(route, {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  public isWidget(route: string | unknown) {
    if (typeof route !== 'string') return false;
    return this.hasWidgets.includes(route);
  }

  private setActivePage() {
    this.router.events
      .pipe(
        takeUntil(this.onDestroy),
        debounceTime(200),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        console.log('Router Url', event.url);
        const checkUrl = event.url === '/' ? '/dashboard' : event.url;
        this.activeRoute$.next(checkUrl);
        this.activePage$.next(
          this.menuItems(this.showDebugTools)
            .find((i) => i.route.includes(checkUrl))
            ?.label.toUpperCase()
        );
      });
  }
}
